import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Archive,
  Check,
  Edit2,
  FileText,
  Folder,
  MoreVertical,
  Share2,
  Trash2,
  Undo,
  Users,
  X,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Project } from '../../hooks/queries/projectTypes';
import { useUser } from '../../hooks/queries/useAuth';
import {
  useDeleteProject,
  useArchiveProject,
  useUpdateProject,
} from '../../hooks/queries/useProjects';
import { ProjectEditModal } from './ProjectEditModal';
import { ProjectContextModal } from './ProjectContextModal';
import CollaboratorsModal from './CollaboratorsModal';
import { Dropdown, DropdownContent, DropdownItem } from '../ui';
import { useViewport } from '../../contexts/ViewportContext';
import { usePermissions } from '../../hooks/queries/usePermissions';
import { logger } from '../../utils/logger';
import { useToastUIStore } from '../../store/toastUIStore';

interface ProjectItemProps {
  project: Project;
  isActive: boolean;
  onClick: () => void;
  onUpdate?: (id: string, updates: Partial<Project>) => Promise<void>; // Make onUpdate optional
  projectRole?: 'owner' | 'guest'; // Simplified to match DB roles
}

export function ProjectItem({
  project,
  isActive,
  onClick,
  onUpdate,
  projectRole = 'owner', // Default to owner
}: ProjectItemProps) {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isContextOpen, setIsContextOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { isMobile } = useViewport();
  const { addToast } = useToastUIStore();

  // React Query hooks
  useUser(); // Called for user state initialization
  const {
    permissions,
    loading: permissionsLoading,
    refetch: refetchPermissions,
  } = usePermissions();
  const deleteProjectMutation = useDeleteProject();
  const archiveProjectMutation = useArchiveProject();
  const updateProjectMutation = useUpdateProject();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  };

  const handleUpdate = async (name: string, description: string, avatarUrl?: string) => {
    try {
      setIsSubmitting(true);

      // Using React Query mutation for update
      await updateProjectMutation.mutateAsync({
        projectId: project.id,
        updates: {
          name,
          description,
          ...(avatarUrl && { avatar_url: avatarUrl }),
        },
      });

      // Only call onUpdate if explicitly provided (legacy support)
      if (onUpdate) {
        try {
          await onUpdate(project.id, {
            name,
            description,
            ...(avatarUrl && { avatar_url: avatarUrl }),
          });
        } catch (legacyError) {
          // If legacy update fails, log but continue since React Query mutation succeeded
          logger.error(
            'Legacy onUpdate callback failed but React Query update succeeded',
            legacyError
          );
        }
      }

      setIsEditing(false);
    } catch (error) {
      logger.error('Error updating project:', error);
      addToast({
        type: 'error',
        title: 'Update failed',
        message: 'Failed to update project. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContextUpdate = async (context: string) => {
    try {
      // Using React Query mutation for update
      await updateProjectMutation.mutateAsync({
        projectId: project.id,
        updates: { context },
      });

      // Only call onUpdate if explicitly provided (legacy support)
      if (onUpdate) {
        try {
          await onUpdate(project.id, { context });
        } catch (legacyError) {
          // If legacy update fails, log but continue since React Query mutation succeeded
          logger.error(
            'Legacy onUpdate callback failed but React Query update succeeded',
            legacyError
          );
        }
      }

      setIsContextOpen(false);
    } catch (error) {
      logger.error('Error updating project context:', error);
      addToast({
        type: 'error',
        title: 'Update failed',
        message: 'Failed to update project context. Please try again.',
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteProjectMutation.mutateAsync(project.id);
      navigate('/project');
      addToast({
        type: 'success',
        title: 'Project deleted',
        message: 'Project has been deleted successfully',
      });
    } catch (error) {
      logger.error('Error deleting project:', error);
      addToast({
        type: 'error',
        title: 'Delete failed',
        message: 'Failed to delete project. Please try again.',
      });
    }
  };

  const handleArchive = async () => {
    try {
      await archiveProjectMutation.mutateAsync({ projectId: project.id, archive: true });
      navigate('/project');
      addToast({
        type: 'success',
        title: 'Project archived',
        message: 'Project has been archived',
      });
    } catch (error) {
      logger.error('Error archiving project:', error);
      addToast({
        type: 'error',
        title: 'Archive failed',
        message: 'Failed to archive project. Please try again.',
      });
    }
  };

  const handleUnarchive = async () => {
    try {
      await archiveProjectMutation.mutateAsync({ projectId: project.id, archive: false });
      navigate('/project');
      addToast({
        type: 'success',
        title: 'Project unarchived',
        message: 'Project has been unarchived',
      });
    } catch (error) {
      logger.error('Error unarchiving project:', error);
      addToast({
        type: 'error',
        title: 'Unarchive failed',
        message: 'Failed to unarchive project. Please try again.',
      });
    }
  };

  const handleShareClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(false);

    logger.component('Project share button clicked', {
      projectId: project.id,
      projectName: project.name,
      projectRole,
    });

    // Check if permissions are still loading
    if (permissionsLoading) {
      logger.component('Permissions loading in progress, waiting...', {
        projectId: project.id,
      });
      // Add a loading toast to indicate permission check is in progress
      addToast({
        type: 'info',
        title: 'Checking permissions',
        message: 'Please wait while we check your permissions...',
      });
      return;
    }

    // Ensure we have permissions data - refetch if needed
    const currentPermissions = permissions;
    if (!currentPermissions) {
      logger.component('No permissions found, fetching...', {
        projectId: project.id,
      });

      // Using React Query refetch now
      try {
        refetchPermissions();
        // No need to wait for result, the component will re-render when the data is available
        logger.component('Permissions refetch initiated', {
          projectId: project.id,
        });

        // Add a loading toast to indicate permission check is in progress
        addToast({
          type: 'info',
          title: 'Checking permissions',
          message: 'Please wait while we check your permissions...',
        });
        return;
      } catch (error) {
        logger.error('Error requesting permissions refetch for project sharing', {
          projectId: project.id,
          error: error instanceof Error ? error.message : String(error),
        });
        addToast({
          type: 'error',
          title: 'Permission check failed',
          message: 'Could not verify your account permissions. Please try again.',
        });
        return;
      }
    }

    // Check if user can share projects (Team plan or VIP)
    const hasTeamPlan =
      currentPermissions?.subscriptionTier &&
      ['SMALL_TEAM', 'MEDIUM_TEAM', 'LARGE_TEAM'].includes(currentPermissions.subscriptionTier);
    const canShareProjects = hasTeamPlan || currentPermissions?.isVIP;

    logger.component('Project sharing permission check', {
      projectId: project.id,
      hasTeamPlan,
      isVIP: currentPermissions?.isVIP,
      canShare: canShareProjects,
    });

    if (!canShareProjects) {
      logger.component('User does not have sharing permission, redirecting to subscription page', {
        projectId: project.id,
      });

      navigate('/subscription', {
        state: {
          reason: 'project_sharing',
          message:
            'Project sharing is only available with Team plans. Upgrade to enable collaboration.',
        },
        replace: true,
      });
      return;
    }

    // If authorized, open share modal
    logger.component('Opening share modal', { projectId: project.id });
    setIsShareOpen(true);
  };

  return (
    <>
      <div
        data-testid={`project-${project.id}`}
        data-project-id={project.id}
        className={cn(
          'relative p-3 hover:bg-transparent transition-colors cursor-pointer group project-item',
          isActive && 'bg-transparent',
          (isMenuOpen || isDeleting) && 'z-[100]'
        )}
        onClick={handleClick}
      >
        <div className="flex items-center gap-3">
          {project.avatar_url ? (
            <img
              src={project.avatar_url}
              alt={project.name}
              className="w-10 h-10 rounded-full object-cover flex-shrink-0 shadow transition-shadow duration-300 group-hover:shadow-md"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 shadow transition-shadow duration-300 group-hover:shadow-md">
              <Folder size={20} className="text-gray-600" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p
              className={cn(
                'text-base font-medium truncate tracking-tight',
                isActive && 'font-bold',
                isMobile && 'text-lg'
              )}
              style={{ color: 'var(--text-primary)' }}
            >
              {project.name}
            </p>
            {project.description && (
              <p className={cn('text-xs text-gray-400 truncate', isMobile && 'text-sm')}>
                {project.description}
              </p>
            )}
          </div>
        </div>

        <div className="absolute right-4 top-1/2 -translate-y-1/2">
          {/* Share icon that's visible by default but hidden on hover */}
          {project.isShared && projectRole === 'owner' && (
            <div className="absolute w-6 h-6 flex items-center justify-center opacity-70 group-hover:opacity-0 transition-opacity pointer-events-none">
              <div className="p-[5px]">
                <Users size={16} className="text-gray-400" />
              </div>
            </div>
          )}
          {/* Ellipsis button that's hidden by default but shown on hover */}
          <button
            ref={buttonRef}
            aria-label="Project actions"
            onClick={e => {
              e.stopPropagation();
              setIsMenuOpen(!isMenuOpen);
            }}
            className={cn(
              'p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity',
              'hover:bg-gray-200 text-gray-600 hover:text-gray-900',
              isMenuOpen && 'opacity-100 bg-gray-200 text-gray-900'
            )}
          >
            <MoreVertical size={16} />
          </button>

          <Dropdown
            trigger={<span style={{ display: 'none' }} />}
            open={isMenuOpen}
            onOpenChange={setIsMenuOpen}
            width={144} // w-36 in pixels
            isMobile={isMobile}
            position="bottom-right"
            showBackdrop={isMobile}
            mobilePosition={0.2}
            mobileTitle="Project Options"
            anchorElement={buttonRef.current}
            contentClassName="bg-white rounded-lg shadow-lg overflow-hidden z-[110]"
          >
            <DropdownContent>
              {projectRole === 'owner' && (
                <>
                  <DropdownItem
                    leftIcon={<Edit2 size={16} />}
                    onClick={e => {
                      e.stopPropagation();
                      setIsMenuOpen(false);
                      setIsEditing(true);
                    }}
                    className="text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  >
                    Edit
                  </DropdownItem>

                  <DropdownItem
                    leftIcon={<FileText size={16} />}
                    onClick={e => {
                      e.stopPropagation();
                      setIsMenuOpen(false);
                      setIsContextOpen(true);
                    }}
                    className="text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  >
                    Context
                  </DropdownItem>

                  <DropdownItem
                    leftIcon={<Share2 size={16} />}
                    onClick={e => {
                      e.stopPropagation();
                      setIsMenuOpen(false);
                      handleShareClick(e);
                    }}
                    className="text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  >
                    Share
                  </DropdownItem>

                  <DropdownItem
                    leftIcon={project.archived ? <Undo size={16} /> : <Archive size={16} />}
                    onClick={e => {
                      e.stopPropagation();
                      setIsMenuOpen(false);
                      project.archived ? handleUnarchive() : handleArchive();
                    }}
                    className="text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  >
                    {project.archived ? 'Unarchive' : 'Archive'}
                  </DropdownItem>

                  <DropdownItem
                    leftIcon={<Trash2 size={16} className="text-red-600" />}
                    onClick={e => {
                      e.stopPropagation();
                      setIsMenuOpen(false);
                      setIsDeleting(true);
                    }}
                    className="text-red-600 hover:bg-gray-100 hover:text-red-700"
                  >
                    Delete
                  </DropdownItem>
                </>
              )}

              {projectRole === 'guest' && (
                <>
                  <DropdownItem
                    leftIcon={<Edit2 size={16} />}
                    onClick={e => {
                      e.stopPropagation();
                      setIsMenuOpen(false);
                      setIsEditing(true);
                    }}
                    className="text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  >
                    Edit
                  </DropdownItem>

                  <DropdownItem
                    leftIcon={<FileText size={16} />}
                    onClick={e => {
                      e.stopPropagation();
                      setIsMenuOpen(false);
                      setIsContextOpen(true);
                    }}
                    className="text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  >
                    Context
                  </DropdownItem>
                </>
              )}
            </DropdownContent>
          </Dropdown>

          {isDeleting && (
            <div
              className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1 bg-white rounded-lg shadow-lg p-1 z-[110]"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={e => {
                  e.stopPropagation();
                  handleDelete();
                }}
                className="p-1 rounded hover:bg-gray-100 text-red-600 hover:text-red-700"
                title="Confirm delete"
              >
                <Check size={16} />
              </button>
              <button
                onClick={e => {
                  e.stopPropagation();
                  setIsDeleting(false);
                }}
                className="p-1 rounded hover:bg-gray-100 text-gray-600 hover:text-gray-900"
                title="Cancel"
              >
                <X size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      <ProjectEditModal
        isOpen={isEditing}
        onClose={() => setIsEditing(false)}
        project={project}
        onSubmit={handleUpdate}
        isSubmitting={isSubmitting}
      />

      <ProjectContextModal
        isOpen={isContextOpen}
        onClose={() => setIsContextOpen(false)}
        project={project}
        onSubmit={handleContextUpdate}
      />

      {isShareOpen && (
        <CollaboratorsModal
          projectId={project.id}
          projectName={project.name}
          onClose={() => setIsShareOpen(false)}
        />
      )}
    </>
  );
}
